import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { formatCurrency } from "../../utils";
import { SchoolFeeFeeLine } from "../../models/schoolFee";

// Every fee the selected student's enrolments carry, flattened in the order
// the server sent them.
//
// ⛔ LOCKED (TODO-187, resolved 2026-08-25 by PR #738): the server returns only
// the fees of the service the payer arrived through, so there is NO client-side
// grouping or filtering by `variant` here. Duplicating a server rule in a
// second place is how the two drift, and the payer would then see a set the
// payment leg disagrees with.
const applicableFees = (
  enrolments: { fees: SchoolFeeFeeLine[] }[]
): SchoolFeeFeeLine[] => enrolments.flatMap(enrolment => enrolment.fees);

/**
 * PAY-05. Exactly one fee is payable per payment in V1, so this is a radio
 * group and not a checklist -- the single-selection rule is expressed by the
 * control rather than validated after the fact.
 */
const FeeSelect = () => {
  const { t } = useTranslation();
  const { student, selectedFee, selectFee } = useSchoolFee();

  const fees = applicableFees(student?.enrolments ?? []);

  if (fees.length === 0) {
    return (
      <Box mb={4}>
        <Typography variant="body1" fontWeight="700">
          {t("schoolfee.fee.none_title")}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          {t("schoolfee.fee.none_body")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        {t("schoolfee.fee.select_label")}
      </Typography>

      <RadioGroup
        value={selectedFee?.fee_assignment_id ?? ""}
        onChange={event => {
          const chosen = fees.find(
            fee => fee.fee_assignment_id === event.target.value
          );

          // selectFee already drops a stale amount when the fee changes; that
          // invalidation is not repeated here, because two places deciding
          // when an amount is stale is how one of them stops.
          selectFee(
            chosen
              ? {
                  fee_assignment_id: chosen.fee_assignment_id,
                  name: chosen.name,
                  variant: chosen.variant,
                  amount: chosen.amount,
                  currency: chosen.currency,
                  supports_partial_payments: chosen.supports_partial_payments,
                }
              : null
          );
        }}
      >
        {fees.map(fee => (
          <FormControlLabel
            key={fee.fee_assignment_id}
            value={fee.fee_assignment_id}
            control={<Radio />}
            label={`${fee.name} — ${formatCurrency(
              fee.amount,
              undefined,
              fee.currency
            )}`}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};

export default FeeSelect;
